// /app/frontend/src/components/frugal-v2/sections/ExpensesSection.jsx
// Recurring expenses section with account selection on payment (< 150 lines)

import React, { useState } from 'react';
import { Receipt } from 'lucide-react';
import CollapsibleSection from '../components/CollapsibleSection';
import ListItem from '../components/ListItem';
import AddItemForm from '../components/AddItemForm';
import EditItemModal from '../components/EditItemModal';
import AccountSelector from '../components/AccountSelector';
import { EXPENSE_CATEGORIES } from '../constants/categories';

const ExpensesSection = ({ 
  expenses = [], 
  onAdd, 
  onToggle, 
  onEdit, 
  onDelete,
  token,
  fundingStatus,
  t 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [payingExpense, setPayingExpense] = useState(null);

  const total = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const paid = expenses
    .filter(e => e.is_paid)
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  const subtitle = `${t('frugal.expenses.paid')}: $${paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Sort: overdue first, then by day
  const sortedExpenses = [...expenses].sort((a, b) => {
    if (a.is_paid !== b.is_paid) return a.is_paid ? 1 : -1;
    if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
    return (a.days_until || 0) - (b.days_until || 0);
  });

  const handleToggle = (id) => {
    const expense = expenses.find(e => e.id === id);
    if (expense && !expense.is_paid) {
      // Show account selector when marking as paid
      setPayingExpense(expense);
    } else {
      // Unpaying - no account needed
      onToggle(id, null);
    }
  };

  const handleAccountSelect = async (account) => {
    if (payingExpense) {
      await onToggle(payingExpense.id, account.id);
      setPayingExpense(null);
    }
  };

  const handleSaveEdit = async (id, data) => {
    await onEdit(id, data);
    setEditingItem(null);
  };

  const addFormComponent = (
    <AddItemForm
      type="expense"
      onAdd={async (data) => {
        await onAdd(data);
        setShowForm(false);
      }}
      onCancel={() => setShowForm(false)}
      colorClass="bg-rose-500 hover:bg-rose-600"
      showCategory={true}
      categories={EXPENSE_CATEGORIES}
      t={t}
    />
  );

  return (
    <>
      <CollapsibleSection
        icon={Receipt}
        title={t('frugal.expenses.title')}
        total={total}
        subtitle={subtitle}
        iconColorClass="text-rose-400"
        showAddForm={showForm}
        onToggleAddForm={() => setShowForm(!showForm)}
        addForm={addFormComponent}
        defaultExpanded={false}
        fundingStatus={fundingStatus}
      >
        {/* List */}
        <div className="space-y-2">
          {sortedExpenses.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">{t('frugal.expenses.empty')}</p>
          ) : (
            sortedExpenses.map(expense => (
              <ListItem
                key={expense.id}
                item={expense}
                colorClass="text-rose-400"
                onToggle={handleToggle}
                onEdit={setEditingItem}
                onDelete={onDelete}
                showStatus={true}
                showMenu={true}
                t={t}
              />
            ))
          )}
        </div>
      </CollapsibleSection>

      {/* Edit Modal */}
      {editingItem && (
        <EditItemModal
          item={editingItem}
          type="expense"
          onSave={handleSaveEdit}
          onDelete={onDelete}
          onClose={() => setEditingItem(null)}
          showCategory={true}
          categories={EXPENSE_CATEGORIES}
          t={t}
        />
      )}

      {/* Account Selector Modal */}
      <AccountSelector
        isOpen={!!payingExpense}
        onClose={() => setPayingExpense(null)}
        onSelect={handleAccountSelect}
        amount={payingExpense?.amount}
        description={payingExpense?.name}
        token={token}
        t={t}
      />
    </>
  );
};

export default ExpensesSection;
